import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATES, PROFESSIONS } from "@/lib/constants";
import { Label } from "@/components/site/IntakeSteps";
import { toast } from "sonner";

const BLANK = { name: "", firm: "", profession: "", email: "", phone: "", website: "", city: "", state: "", bio: "" };

const Field = ({ label, k, f, set, ...rest }) => (
  <div className={rest.wide ? "sm:col-span-2" : ""}>
    <Label>{label}</Label>
    <input className="field" value={f[k]} onChange={set(k)} data-testid={`partner-${k}-input`} type={rest.type || "text"} />
  </div>
);

export default function PartnerIntake() {
  const { user, profile, partner, setPartner } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (partner === null || f) {
      return;
    }
    setF(partner ? Object.fromEntries(Object.keys(BLANK).map((k) => [k, partner[k] ?? ""])) : { ...BLANK, name: user?.name || "", email: user?.email || "" });
  }, [partner, user, f]);

  if (profile) return <Navigate to="/dashboard" replace />;
  if (!f) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.profession || !f.state) return toast.error("Pick your profession and state");
    if (f.bio.trim().length < 20) return toast.error("Bio must be at least 20 characters");
    setBusy(true);
    try {
      const { data } = await api.put("/me/partner", f);
      setPartner(data);
      toast.success(partner ? "Profile updated" : "Application submitted — we'll review it shortly");
      navigate("/dashboard");
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="partner-intake-page">
      <span className="eyebrow">The 1031 Club · referral partners</span>
      <h1 className="font-display text-4xl font-black tracking-tight mt-4">{partner ? "Update your partner profile" : "Join as a referral partner"}</h1>
      <p className="text-white/55 mt-3 max-w-xl">For CPAs, attorneys, brokers and advisors who work with 1031 investors. Free to join. Once approved, you can chat with QIs and send them client referrals.</p>
      <form onSubmit={submit} className="glass-card p-6 md:p-8 mt-8 grid sm:grid-cols-2 gap-4">
        <Field label="Your name *" k="name" f={f} set={set} />
        <Field label="Firm *" k="firm" f={f} set={set} />
        <div><Label>Profession *</Label>
          <select className="field" value={f.profession} onChange={set("profession")} data-testid="partner-profession-select">
            <option value="" className="bg-ink">Select</option>
            {PROFESSIONS.map((p) => <option key={p} value={p} className="bg-ink">{p}</option>)}
          </select>
        </div>
        <Field label="Email *" k="email" type="email" f={f} set={set} />
        <Field label="Phone" k="phone" f={f} set={set} />
        <Field label="Website" k="website" f={f} set={set} />
        <Field label="City" k="city" f={f} set={set} />
        <div><Label>State *</Label>
          <select className="field" value={f.state} onChange={set("state")} data-testid="partner-state-select">
            <option value="" className="bg-ink">Select</option>
            {STATES.map(([c, n]) => <option key={c} value={c} className="bg-ink">{n}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2"><Label>About you * ({f.bio.length}/1000)</Label>
          <textarea rows={4} maxLength={1000} className="field resize-none" placeholder="Who you serve and how you work with 1031 investors." value={f.bio} onChange={set("bio")} data-testid="partner-bio-input" />
        </div>
        <button disabled={busy} className="btn-pink sm:col-span-2" data-testid="partner-submit-button">{busy ? "Saving..." : partner ? "Save changes" : "Submit application"}</button>
      </form>
    </div>
  );
}
